import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { pipelineApi, targetsApi, projectsApi, outreachApi, exportApi, researchApi, briefingApi, formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { WriteOnly } from '../components/ViewerGuard';
import { HelpButton } from '../components/HelpPanel';
import usePageTitle from '../hooks/usePageTitle';
import {
  Plus,
  Flame,
  TrendingUp,
  Snowflake,
  Clock,
  MoreVertical,
  Edit2,
  Trash2,
  Check,
  Loader2,
  X,
  Phone,
  Mail,
  StickyNote,
  ChevronRight,
  Calendar,
  PoundSterling,
  Percent,
  Target,
  User,
  Building,
  FileText
} from 'lucide-react';
import BriefingModal from '../components/BriefingModal';
import SearchableSelect from '../components/SearchableSelect';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../components/ui/sheet';

const STAGES = [
  { id: 'identified', label: 'Identified', color: '#A3B5C7', probability: 5 },
  { id: 'researched', label: 'Researched', color: '#6BCBFF', probability: 10 },
  { id: 'outreach_sent', label: 'Outreach Sent', color: '#FFD93D', probability: 20 },
  { id: 'response_received', label: 'Response', color: '#00D4C8', probability: 35 },
  { id: 'meeting_booked', label: 'Meeting Booked', color: '#00D4C8', probability: 50 },
  { id: 'proposal_sent', label: 'Proposal Sent', color: 'white', probability: 65 },
  { id: 'active', label: 'Active', color: '#33DDD3', probability: 80 },
  { id: 'closed_won', label: 'Won', color: '#22C55E', probability: 100 },
  { id: 'closed_lost', label: 'Lost', color: '#FF6B6B', probability: 0 },
];

const DEFAULT_FLOW = [
  { id: 'identified', label: 'Identified', color: '#A3B5C7', probability: 5 },
  { id: 'researched', label: 'Researched', color: '#6BCBFF', probability: 10 },
  { id: 'outreach_sent', label: 'Outreach Sent', color: '#FFD93D', probability: 20 },
  { id: 'response_received', label: 'Response', color: '#00D4C8', probability: 35 },
  { id: 'meeting_booked', label: 'Meeting Booked', color: '#00D4C8', probability: 50 },
  { id: 'proposal_sent', label: 'Proposal Sent', color: 'white', probability: 65 },
  { id: 'active', label: 'Active', color: '#33DDD3', probability: 80 },
  { id: 'closed_won', label: 'Won', color: '#22C55E', probability: 100 },
  { id: 'closed_lost', label: 'Lost', color: '#FF6B6B', probability: 0 },
];
const INVESTOR_FLOW = [
  { id: 'identified', label: 'Identified', color: '#A3B5C7', probability: 5 },
  { id: 'researched', label: 'Researched', color: '#6BCBFF', probability: 10 },
  { id: 'warm_intro_sought', label: 'Warm Intro Sought', color: '#FFD93D', probability: 15 },
  { id: 'outreach_sent', label: 'Outreach Sent', color: '#FFD93D', probability: 25 },
  { id: 'meeting_booked', label: 'Meeting Booked', color: '#00D4C8', probability: 40 },
  { id: 'deck_presented', label: 'Deck Presented', color: '#00D4C8', probability: 55 },
  { id: 'due_diligence', label: 'Due Diligence', color: '#33DDD3', probability: 70 },
  { id: 'term_sheet', label: 'Term Sheet', color: '#33DDD3', probability: 85 },
  { id: 'closed', label: 'Closed', color: '#22C55E', probability: 100 },
];
const GRANTS_FLOW = [
  { id: 'identified', label: 'Identified', color: '#A3B5C7', probability: 5 },
  { id: 'eligibility_confirmed', label: 'Eligibility Confirmed', color: '#6BCBFF', probability: 15 },
  { id: 'application_in_progress', label: 'Application In Progress', color: '#FFD93D', probability: 30 },
  { id: 'submitted', label: 'Submitted', color: '#00D4C8', probability: 50 },
  { id: 'under_review', label: 'Under Review', color: '#33DDD3', probability: 65 },
  { id: 'awarded', label: 'Awarded', color: '#22C55E', probability: 100 },
  { id: 'rejected', label: 'Rejected', color: '#FF6B6B', probability: 0 },
];
// Post-sale client lifecycle. Distinct client_* ids so they never collide with the
// standard BD flow's stage ids. Mirrors backend CLIENTS_PIPELINE_STAGES.
const CLIENTS_FLOW = [
  { id: 'client_onboarded', label: 'Onboarded', color: '#6BCBFF', probability: 100 },
  { id: 'client_active', label: 'Active', color: '#33DDD3', probability: 100 },
  { id: 'client_renewal', label: 'Renewal/Expansion', color: '#FFD93D', probability: 100 },
  { id: 'client_churned', label: 'Churned', color: '#FF6B6B', probability: 0 },
];

// Category -> Kanban stage flow. Categories not mapped use the standard BD flow.
// Mirrors backend services/taxonomy.CATEGORY_TO_STAGES.
const STAGE_SETS = {
  default: { label: 'All Pipelines', stages: DEFAULT_FLOW },
  'Clients': { label: 'Clients', stages: CLIENTS_FLOW },
  'Prospective Clients': { label: 'Prospective Clients', stages: DEFAULT_FLOW },
  'Investors': { label: 'Investors', stages: INVESTOR_FLOW },
  'Grants & Funding': { label: 'Grants & Funding', stages: GRANTS_FLOW },
  'Partnerships': { label: 'Partnerships', stages: DEFAULT_FLOW },
  'Other / Unclassified': { label: 'Other / Unclassified', stages: DEFAULT_FLOW },
};

// The six white-label default categories (operator picks one per record).
const PIPELINE_CATEGORIES = [
  'Clients', 'Prospective Clients', 'Investors', 'Grants & Funding', 'Partnerships', 'Other / Unclassified',
];

// Get the stage-flow key for a record based on its authoritative category.
const getStageSetKey = (record) => {
  const cat = record?.category || '';
  if (STAGE_SETS[cat]) return cat;
  return 'default';
};

const TEMPERATURES = [
  { id: 'hot', label: 'Hot', icon: Flame, color: '#FF6B6B', emoji: '🔴' },
  { id: 'warm', label: 'Warm', icon: TrendingUp, color: '#FFD93D', emoji: '🟡' },
  { id: 'cold', label: 'Cold', icon: Snowflake, color: '#6BCBFF', emoji: '🔵' },
  { id: 'dormant', label: 'Dormant', icon: Clock, color: '#6B7280', emoji: '⚫' },
];

const BD_WORK_TYPES = ['Commission', 'Co-Pro', 'Brand', 'Financing', 'Screening', 'Product Integration'];
const DEAL_TYPES = ['one_off', 'recurring', 'retainer'];

const STAGE_PTS = { identified: 5, researched: 10, outreach_sent: 15, response_received: 20, meeting_booked: 23, proposal_sent: 26, active: 28, closed_won: 30, closed_lost: 0 };
const computeScore = (r) => {
  let s = STAGE_PTS[r.stage] || 5;
  if (r.last_contact_date) {
    const d = Math.floor((Date.now() - new Date(r.last_contact_date).getTime()) / 86400000);
    s += d <= 3 ? 30 : d <= 7 ? 25 : d <= 14 ? 20 : d <= 30 ? 12 : d <= 60 ? 5 : d <= 90 ? 2 : 0;
  }
  if (['response_received', 'meeting_booked', 'proposal_sent', 'active', 'closed_won'].includes(r.stage)) s += 10;
  return Math.min(s, 100);
};
const scoreColor = (s) => s >= 71 ? 'bg-green-500/20 text-green-400 border-green-500/30' : s >= 41 ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' : 'bg-red-500/20 text-red-400 border-red-500/30';

// Pipeline board stale-lead indicator threshold. Independent of dashboard.py's
// 14-day "needs attention" widget — this is a broader neglect scan at 21 days.
const STALE_LEAD_DAYS = 21;
const isStaleRecord = (r) => {
  if (['closed_won', 'closed_lost'].includes(r.stage)) return false;
  if (!r.last_contact_date) return true;
  const days = Math.floor((Date.now() - new Date(r.last_contact_date).getTime()) / 86400000);
  return days > STALE_LEAD_DAYS;
};

// deal_value gating mirrors backend models.human_value_eligible: Clients may carry a
// value at any stage; everything else only from Proposal Sent (VALUE_ELIGIBLE_STAGES) on.
const VALUE_ELIGIBLE_STAGES = new Set(['proposal_sent', 'active', 'closed_won', 'closed_lost', 'due_diligence', 'term_sheet', 'closed']);
const ALWAYS_VALUE_CATEGORIES = new Set(['Clients']);
const isValueEligible = (category, stage) => ALWAYS_VALUE_CATEGORIES.has(category) || VALUE_ELIGIBLE_STAGES.has(stage);

// Owner canonicalisation — mirrors backend services/owner_util.canonical_owner.
// Generic (Title Case + "Unassigned"); assignable owners come from the real users list.
const canonicalOwner = (value) => {
  if (!value || !String(value).trim()) return 'Unassigned';
  const raw = String(value).trim();
  if (['unassigned', 'unknown'].includes(raw.toLowerCase())) return 'Unassigned';
  return raw.replace(/\b\w/g, (c) => c.toUpperCase());
};

const PipelinePage = () => {
  usePageTitle('Pipeline');
  const { user } = useAuth();
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [targets, setTargets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [skippedCount, setSkippedCount] = useState(0);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [draggingId, setDraggingId] = useState(null);
  const [showDeleteTargetConfirm, setShowDeleteTargetConfirm] = useState(false);
  const [deletingTarget, setDeletingTarget] = useState(false);
  const [briefingTarget, setBriefingTarget] = useState(null);
  const [refreshingIntel, setRefreshingIntel] = useState(null);
  const [activeStageSet, setActiveStageSet] = useState('default');
  const [inlineNoteText, setInlineNoteText] = useState('');
  const [editingNextAction, setEditingNextAction] = useState(false);
  const [nextActionDraft, setNextActionDraft] = useState('');
  const [ownerOptions, setOwnerOptions] = useState(['Unassigned']);
  const [tempFilter, setTempFilter] = useState([]);
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState('activity');

  const [formData, setFormData] = useState({
    target_id: '',
    contact_name: '',
    bd_work_types: ['Commission'],
    project_ids: [],
    owner: 'Unassigned',
    stage: 'identified',
    temperature: 'cold',
    next_action: '',
    next_action_due: '',
    notes: '',
    deal_value: '',
    deal_type: 'one_off',
    category: 'Other / Unclassified',
  });

  useEffect(() => {
    fetchData();
    pipelineApi.ownerOptions()
      .then(r => setOwnerOptions(r.data?.owners || ['Unassigned']))
      .catch(() => setOwnerOptions(['Unassigned']));
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [pipelineRes, targetsRes, projectsRes] = await Promise.all([
        pipelineApi.getAll(),
        targetsApi.getAll(),
        projectsApi.getAll(),
      ]);
      const normalizedRecords = pipelineRes.data.map(r => ({
        ...r,
        bd_work_types: r.bd_work_types || (r.bd_work_type ? [r.bd_work_type] : [])
      }));
      setRecords(normalizedRecords);
      setSkippedCount(parseInt(pipelineRes.headers?.['x-pipeline-skipped'] || '0', 10) || 0);
      setTargets(targetsRes.data);
      setProjects(projectsRes.data);
    } catch (err) {
      console.error('Pipeline fetch error:', err);
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdate = async () => {
    setError('');
    try {
      const payload = {
        ...formData,
        deal_value: formData.deal_value ? parseFloat(formData.deal_value) : null,
        next_action_due: formData.next_action_due || null,
      };
      if (editingRecord) {
        await pipelineApi.update(editingRecord.id, payload);
      } else {
        await pipelineApi.create(payload);
      }
      await fetchData();
      closeDialog();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this pipeline record?')) return;
    try {
      await pipelineApi.delete(id);
      if (selectedRecord?.id === id) setSelectedRecord(null);
      await fetchData();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const handleStageChange = async (recordId, newStage) => {
    try {
      await pipelineApi.update(recordId, { stage: newStage });
      setRecords(prev =>
        prev.map(r => (r.id === recordId ? { ...r, stage: newStage } : r))
      );
      if (selectedRecord?.id === recordId) {
        setSelectedRecord(prev => ({ ...prev, stage: newStage }));
      }
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const handleLogContact = async (recordId) => {
    try {
      await pipelineApi.logContact(recordId);
      await fetchData();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const handleResolveSuggestion = async (recordId, accept, e) => {
    if (e) e.stopPropagation();
    try {
      const res = await pipelineApi.resolveSuggestedTemperature(recordId, accept);
      const updated = res.data;
      setRecords(prev => prev.map(r => (r.id === recordId ? { ...r, temperature: updated.temperature, suggested_temperature: undefined } : r)));
      if (selectedRecord?.id === recordId) {
        setSelectedRecord(prev => ({ ...prev, temperature: updated.temperature, suggested_temperature: undefined }));
      }
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const handleAddNote = async () => {
    if (!selectedRecord || !noteText.trim()) return;
    try {
      await pipelineApi.addNote(selectedRecord.id, noteText.trim());
      setNoteText('');
      setShowNoteDialog(false);
      await fetchData();
      // Refresh selected record
      const updated = records.find(r => r.id === selectedRecord.id);
      if (updated) setSelectedRecord(updated);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const handleGenerateOutreach = (record) => {
    const projectId = record.project_ids?.[0] || projects[0]?.id;
    navigate(`/outreach?target_id=${record.target_id}&project_id=${projectId}&pipeline_id=${record.id}`);
  };

  const handleRefreshIntel = async (record) => {
    if (!record.target_name) return;
    setRefreshingIntel(record.id);
    try {
      await researchApi.run(record.target_name);
      await fetchData();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setRefreshingIntel(null);
    }
  };

  const handleGenerateBriefing = (record) => {
    if (!record.target_id) return;
    setBriefingTarget({ id: record.target_id, name: record.target_name || 'Unknown' });
  };

  const handleDeleteTargetFromPanel = async () => {
    if (!selectedRecord?.target_id) return;
    setDeletingTarget(true);
    try {
      await targetsApi.delete(selectedRecord.target_id);
      setShowDeleteTargetConfirm(false);
      setSelectedRecord(null);
      await fetchData();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setDeletingTarget(false);
    }
  };

  const openCreateDialog = () => {
    setEditingRecord(null);
    setFormData({
      target_id: '',
      contact_name: '',
      bd_work_types: ['Commission'],
      project_ids: [],
      owner: user?.name || 'Unassigned',
      stage: 'identified',
      temperature: 'cold',
      next_action: '',
      next_action_due: '',
      notes: '',
      deal_value: '',
      deal_type: 'one_off',
      category: 'Other / Unclassified',
    });
    setShowCreateDialog(true);
  };

  const openEditDialog = (record) => {
    setEditingRecord(record);
    setFormData({
      target_id: record.target_id,
      contact_name: record.contact_name || '',
      bd_work_types: record.bd_work_types || ['Commission'],
      project_ids: record.project_ids || [],
      owner: record.owner,
      stage: record.stage,
      temperature: record.temperature,
      next_action: record.next_action || '',
      next_action_due: record.next_action_due ? record.next_action_due.split('T')[0] : '',
      notes: record.notes || '',
      deal_value: record.deal_value || '',
      deal_type: record.deal_type || 'one_off',
      category: record.category || 'Other / Unclassified',
    });
    setShowCreateDialog(true);
  };

  const closeDialog = () => {
    setShowCreateDialog(false);
    setEditingRecord(null);
    setError('');
  };

  const toggleBdWorkType = (type) => {
    setFormData(prev => {
      const current = prev.bd_work_types || [];
      if (current.includes(type)) {
        if (current.length === 1) return prev;
        return { ...prev, bd_work_types: current.filter(t => t !== type) };
      } else {
        return { ...prev, bd_work_types: [...current, type] };
      }
    });
  };

  const renderSuggestion = (record) => {
    const s = record.suggested_temperature;
    if (!s || s === record.temperature) return null;
    const cfg = TEMPERATURES.find(t => t.id === s);
    if (!cfg) return null;
    return (
      <div
        className="flex items-center justify-between gap-2 mb-2 px-2 py-1.5 rounded-md bg-[#0E1C33] border border-dashed border-[#2A4055]"
        onClick={(e) => e.stopPropagation()}
        data-testid={`suggestion-banner-${record.id}`}
      >
        <span className="text-[11px] text-[#A3B5C7]">
          AI suggests <span style={{ color: cfg.color }} className="font-semibold uppercase">{cfg.label}</span>
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => handleResolveSuggestion(record.id, true, e)}
            className="p-1 rounded hover:bg-[#00D4C8]/15 text-[#00D4C8]"
            title="Accept suggestion"
            data-testid={`suggestion-accept-${record.id}`}
          >
            <Check className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
          <button
            onClick={(e) => handleResolveSuggestion(record.id, false, e)}
            className="p-1 rounded hover:bg-red-500/15 text-[#A3B5C7] hover:text-red-400"
            title="Dismiss suggestion"
            data-testid={`suggestion-dismiss-${record.id}`}
          >
            <X className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
        </div>
      </div>
    );
  };

  const renderTemperatureTag = (temp, size = 'sm') => {
    const cfg = TEMPERATURES.find(t => t.id === temp) || TEMPERATURES[2];
    const pad = size === 'lg' ? 'px-2.5 py-1 text-xs' : 'px-2 py-0.5 text-[10px]';
    return (
      <span
        className={`inline-flex items-center rounded-md font-semibold uppercase tracking-wide border ${pad}`}
        style={{ color: cfg.color, backgroundColor: `${cfg.color}1A`, borderColor: `${cfg.color}59` }}
        data-testid={`temperature-tag-${temp || 'unknown'}`}
      >
        {cfg.label}
      </span>
    );
  };

  const getTemperatureIcon = (temp) => {
    const tempConfig = TEMPERATURES.find(t => t.id === temp);
    if (!tempConfig) return null;
    const Icon = tempConfig.icon;
    return <Icon className="w-4 h-4" style={{ color: tempConfig.color }} strokeWidth={1.5} />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return `£${value.toLocaleString()}`;
  };

  const getProjectName = (projectId) => {
    return projects.find(p => p.id === projectId)?.name || projectId;
  };

  const getTargetForRecord = (record) => {
    return targets.find(t => t.id === record.target_id);
  };

  // Drag and drop handlers
  const handleDragStart = (e, recordId) => {
    setDraggingId(recordId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, stageId) => {
    e.preventDefault();
    if (draggingId) {
      handleStageChange(draggingId, stageId);
      setDraggingId(null);
    }
  };

  const activeStages = STAGE_SETS[activeStageSet]?.stages || STAGE_SETS.default.stages;

  // Filter records: category-driven. "All Pipelines" shows everything on the default
  // flow; a specific category shows only that category's records on its own flow.
  let filteredRecords = activeStageSet === 'default'
    ? records
    : records.filter(r => (r.category || '') === activeStageSet);

  if (tempFilter.length > 0) {
    filteredRecords = filteredRecords.filter(r => tempFilter.includes(r.temperature));
  }
  if (ownerFilter !== 'all') {
    filteredRecords = filteredRecords.filter(r => canonicalOwner(r.target_owner || r.owner) === ownerFilter);
  }
  if (searchText.trim()) {
    const q = searchText.trim().toLowerCase();
    filteredRecords = filteredRecords.filter(r => (r.target_name || '').toLowerCase().includes(q));
  }

  const boardOwners = Array.from(new Set(records.map(r => canonicalOwner(r.target_owner || r.owner)))).sort();

  const sortRecords = (list) => {
    const arr = [...list];
    if (sortBy === 'newest') {
      arr.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    } else if (sortBy === 'oldest') {
      arr.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
    } else {
      arr.sort((a, b) => new Date(b.last_contact_date || 0) - new Date(a.last_contact_date || 0));
    }
    return arr;
  };

  const recordsByStage = activeStages.reduce((acc, stage) => {
    acc[stage.id] = sortRecords(filteredRecords.filter(r => r.stage === stage.id));
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="">
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-[#00D4C8] animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="font-heading text-3xl text-white" data-testid="pipeline-title">Pipeline</h1>
            <HelpButton />
          </div>
          <p className="text-sm text-[#A3B5C7]/70">Track every live opportunity — drag cards between stages as relationships progress toward deals.</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Category selector — drives both filter and the Kanban stage-flow */}
          <select
            value={activeStageSet}
            onChange={(e) => setActiveStageSet(e.target.value)}
            className="input-field text-xs py-1.5 px-3 w-auto"
            data-testid="category-selector"
          >
            {Object.entries(STAGE_SETS).map(([key, set]) => (
              <option key={key} value={key}>{set.label}</option>
            ))}
          </select>
          <div className="flex items-center gap-1">
            <button
              onClick={async () => {
                try {
                  const res = await exportApi.pipelineCsv();
                  const url = URL.createObjectURL(new Blob([res.data]));
                  const a = document.createElement('a');
                  a.href = url; a.download = 'pipeline_export.csv'; a.click();
                  URL.revokeObjectURL(url);
                } catch (err) { console.error(err); }
              }}
              className="btn-secondary text-xs px-3 py-1.5"
              data-testid="export-csv-btn"
            >
              CSV
            </button>
            <button
              onClick={async () => {
                try {
                  const res = await exportApi.pipelinePdf();
                  const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                  const a = document.createElement('a');
                  a.href = url; a.download = 'pipeline_briefing.pdf'; a.click();
                  URL.revokeObjectURL(url);
                } catch (err) { console.error(err); }
              }}
              className="btn-secondary text-xs px-3 py-1.5"
              data-testid="export-pdf-btn"
            >
              PDF
            </button>
          </div>
          <WriteOnly>
          <button
            onClick={openCreateDialog}
            className="btn-primary flex items-center gap-2"
            data-testid="create-pipeline-btn"
          >
            <Plus className="w-4 h-4" strokeWidth={2} />
            Add Record
          </button>
          </WriteOnly>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400">
          {error}
        </div>
      )}

      {/* Filter & sort bar */}
      <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="pipeline-filter-bar">
        <input
          type="text"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search by name..."
          className="input-field text-xs py-1.5 px-3"
          style={{ width: '16rem' }}
          data-testid="pipeline-search-input"
        />
        <div className="flex items-center gap-1.5" data-testid="temperature-filter-chips">
          {TEMPERATURES.map((t) => {
            const active = tempFilter.includes(t.id);
            return (
              <button
                key={t.id}
                onClick={() => setTempFilter(prev => active ? prev.filter(x => x !== t.id) : [...prev, t.id])}
                className="px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wide border transition-colors"
                style={active
                  ? { color: t.color, backgroundColor: `${t.color}1A`, borderColor: `${t.color}59` }
                  : { color: '#A3B5C7', backgroundColor: 'transparent', borderColor: '#1A2F4C' }}
                data-testid={`temperature-filter-${t.id}`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        <select
          value={ownerFilter}
          onChange={(e) => setOwnerFilter(e.target.value)}
          className="input-field text-xs py-1.5 px-3"
          style={{ width: '11rem' }}
          data-testid="owner-filter-selector"
        >
          <option value="all">All Owners</option>
          {boardOwners.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="input-field text-xs py-1.5 px-3 ml-auto"
          style={{ width: '11rem' }}
          data-testid="sort-selector"
        >
          <option value="activity">Last activity</option>
          <option value="newest">Newest added</option>
          <option value="oldest">Oldest added</option>
        </select>
        {(tempFilter.length > 0 || ownerFilter !== 'all' || searchText.trim()) && (
          <button
            onClick={() => { setTempFilter([]); setOwnerFilter('all'); setSearchText(''); }}
            className="text-xs text-[#A3B5C7] hover:text-white underline"
            data-testid="clear-filters-btn"
          >
            Clear
          </button>
        )}
      </div>

      {skippedCount > 0 && (
        <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm" data-testid="pipeline-skipped-notice">
          {skippedCount} pipeline {skippedCount === 1 ? 'record' : 'records'} could not be displayed due to malformed data and {skippedCount === 1 ? 'was' : 'were'} skipped. The rest of your board is shown normally.
        </div>
      )}

      {/* Kanban board */}
      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="pipeline-kanban">
        {activeStages.map((stage) => (
          <div
            key={stage.id}
            className={`min-w-[280px] kanban-column ${draggingId ? 'border-2 border-dashed border-[#1A2F4C]' : ''}`}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, stage.id)}
            data-testid={`stage-${stage.id}`}
          >
            {/* Column header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: stage.color }}
                />
                <span className="text-sm font-medium text-white">{stage.label}</span>
                <span className="text-xs text-[#A3B5C7]">({stage.probability}%)</span>
              </div>
              <span className="text-xs text-[#A3B5C7] bg-[#0A1628] px-2 py-1 rounded">
                {recordsByStage[stage.id]?.length || 0}
              </span>
            </div>

            {/* Cards */}
            <div className="space-y-3">
              {recordsByStage[stage.id]?.map((record) => (
                <motion.div
                  key={record.id}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  draggable
                  onDragStart={(e) => handleDragStart(e, record.id)}
                  onClick={() => setSelectedRecord(record)}
                  className={`p-4 rounded-lg bg-[#0A1628] border border-[#1A2F4C] hover:border-[#00D4C8]/30 transition-colors group cursor-pointer ${
                    draggingId === record.id ? 'opacity-50' : ''
                  } ${selectedRecord?.id === record.id ? 'border-[#00D4C8]' : ''}`}
                  data-testid={`pipeline-card-${record.id}`}
                >
                  {/* Header with temperature and name */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {renderTemperatureTag(record.temperature)}
                      <span className="text-sm font-medium text-white">
                        {record.target_name || 'Unknown Target'}
                      </span>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button 
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-[#1A2F4C] rounded"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreVertical className="w-4 h-4 text-[#A3B5C7]" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-[#0E1C33] border-[#1A2F4C]">
                        <DropdownMenuItem onClick={() => openEditDialog(record)} className="text-[#D4E2F0] hover:bg-[#1A2F4C]">
                          <Edit2 className="w-4 h-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleLogContact(record.id)} className="text-[#D4E2F0] hover:bg-[#1A2F4C]">
                          <Phone className="w-4 h-4 mr-2" /> Log Contact
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setSelectedRecord(record); setShowNoteDialog(true); }} className="text-[#D4E2F0] hover:bg-[#1A2F4C]">
                          <StickyNote className="w-4 h-4 mr-2" /> Add Note
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleGenerateOutreach(record)} className="text-[#D4E2F0] hover:bg-[#1A2F4C]">
                          <Mail className="w-4 h-4 mr-2" /> Generate Outreach
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleRefreshIntel(record)} className="text-[#D4E2F0] hover:bg-[#1A2F4C]" disabled={refreshingIntel === record.id}>
                          {refreshingIntel === record.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Target className="w-4 h-4 mr-2" />} Refresh Intel
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleGenerateBriefing(record)} className="text-[#00D4C8] hover:bg-[#1A2F4C]">
                          <FileText className="w-4 h-4 mr-2" /> Meeting Brief
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(record.id)} className="text-red-400 hover:bg-red-500/10">
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* BD Work Types */}
                  {renderSuggestion(record)}
                  <div className="flex flex-wrap gap-1 mb-2">
                    {(record.bd_work_types || []).slice(0, 2).map((type) => (
                      <span key={type} className="px-2 py-0.5 text-[10px] uppercase tracking-wide bg-[#00D4C8]/10 text-[#00D4C8] rounded">
                        {type}
                      </span>
                    ))}
                    {(record.bd_work_types || []).length > 2 && (
                      <span className="px-2 py-0.5 text-[10px] text-[#A3B5C7]">+{record.bd_work_types.length - 2}</span>
                    )}
                  </div>

                  {/* Project */}
                  {record.project_ids?.[0] && (
                    <p className="text-xs text-[#A3B5C7] mb-2 truncate">
                      {getProjectName(record.project_ids[0])}
                    </p>
                  )}

                  {/* Footer with dates, value, and owner */}
                  <div className="flex items-center justify-between text-xs text-[#A3B5C7] pt-2 border-t border-[#1A2F4C]">
                    <div className="flex items-center gap-2">
                      <span>Last: {formatDate(record.last_contact_date)}</span>
                      {isStaleRecord(record) && (
                        <span className="inline-flex items-center gap-1 text-amber-400" data-testid={`pipeline-stale-${record.id}`}>
                          <Clock className="w-3 h-3" /> Stale
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {record.target_owner && (
                        <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-wide bg-[#1A2F4C] text-[#A3B5C7] rounded" data-testid={`pipeline-card-owner-${record.id}`}>
                          {record.target_owner}
                        </span>
                      )}
                      {record.deal_value && (
                        <span className="text-[#00D4C8] font-medium">{formatCurrency(record.deal_value)}</span>
                      )}
                      {(() => {
                        const s = computeScore(record);
                        return <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${scoreColor(s)}`} data-testid={`pipeline-score-${record.id}`}>{s}</span>;
                      })()}
                    </div>
                  </div>

                  {/* Next action due */}
                  {record.next_action_due && (
                    <div className="text-xs text-yellow-400 mt-1">
                      Due: {formatDate(record.next_action_due)}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Slide-out Detail Panel */}
      <Sheet open={!!selectedRecord} onOpenChange={(open) => !open && setSelectedRecord(null)}>
        <SheetContent className="bg-[#0E1C33] border-[#1A2F4C] w-[50vw] xl:w-[60vw] sm:max-w-[50vw] xl:max-w-[60vw] overflow-y-auto">
          {selectedRecord && (
            <>
              <SheetHeader>
                <div className="flex items-start justify-between">
                  <SheetTitle className="font-heading text-xl text-white flex items-center gap-3">
                    {renderTemperatureTag(selectedRecord.temperature, 'lg')}
                    {selectedRecord.target_name || 'Unknown Target'}
                  </SheetTitle>
                  <button
                    onClick={() => setShowDeleteTargetConfirm(true)}
                    className="p-2 rounded-lg text-[#A3B5C7] hover:text-red-400 hover:bg-red-400/10 transition-colors shrink-0"
                    title="Delete target"
                    data-testid="pipeline-delete-target-btn"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
                <SheetDescription className="sr-only">Pipeline record details</SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Quick Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleLogContact(selectedRecord.id)}
                    className="flex-1 btn-secondary text-sm py-2 flex items-center justify-center gap-2"
                    title="Updates last contact date + adds to activity log"
                  >
                    <Phone className="w-4 h-4" /> Log Contact / Call
                  </button>
                  <button
                    onClick={() => setShowNoteDialog(true)}
                    className="flex-1 btn-secondary text-sm py-2 flex items-center justify-center gap-2"
                    title="Saved to Notes History below"
                  >
                    <StickyNote className="w-4 h-4" /> Add Note
                  </button>
                  <button
                    onClick={() => handleGenerateOutreach(selectedRecord)}
                    className="flex-1 btn-primary text-sm py-2 flex items-center justify-center gap-2"
                  >
                    <Mail className="w-4 h-4" /> Outreach
                  </button>
                </div>
                {/* Second action row */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleRefreshIntel(selectedRecord)}
                    disabled={refreshingIntel === selectedRecord.id}
                    className="flex-1 btn-secondary text-sm py-2 flex items-center justify-center gap-2"
                    data-testid="pipeline-refresh-intel-btn"
                  >
                    {refreshingIntel === selectedRecord.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Target className="w-4 h-4" />}
                    Refresh Intel
                  </button>
                  <button
                    onClick={() => handleGenerateBriefing(selectedRecord)}
                    className="flex-1 btn-primary text-sm py-2 flex items-center justify-center gap-2"
                    data-testid="pipeline-meeting-brief-btn"
                  >
                    <FileText className="w-4 h-4" /> Meeting Brief
                  </button>
                </div>

                {/* Next Action + Due Date + Temperature (moved to top) */}
                <div className="p-4 rounded-lg bg-[#0A1628] border border-[#1A2F4C]" data-testid="next-action-section">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs uppercase tracking-[0.2em] text-[#00D4C8] mb-1.5">Next Action</label>
                      {editingNextAction ? (
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={nextActionDraft}
                            onChange={e => setNextActionDraft(e.target.value)}
                            className="input-field text-sm flex-1"
                            placeholder="e.g. Send proposal deck"
                            autoFocus
                            onKeyDown={async (e) => {
                              if (e.key === 'Enter') {
                                await pipelineApi.update(selectedRecord.id, { next_action: nextActionDraft });
                                setSelectedRecord(prev => ({ ...prev, next_action: nextActionDraft }));
                                setEditingNextAction(false);
                                fetchData();
                              }
                              if (e.key === 'Escape') setEditingNextAction(false);
                            }}
                            data-testid="next-action-input"
                          />
                          <button
                            onClick={async () => {
                              await pipelineApi.update(selectedRecord.id, { next_action: nextActionDraft });
                              setSelectedRecord(prev => ({ ...prev, next_action: nextActionDraft }));
                              setEditingNextAction(false);
                              fetchData();
                            }}
                            className="btn-primary text-xs px-3"
                          ><Check className="w-3.5 h-3.5" /></button>
                        </div>
                      ) : (
                        <p
                          className="text-white text-sm cursor-pointer hover:text-[#00D4C8] transition-colors min-h-[1.5rem]"
                          onClick={() => { setEditingNextAction(true); setNextActionDraft(selectedRecord.next_action || ''); }}
                          data-testid="next-action-display"
                        >
                          {selectedRecord.next_action || <span className="text-[#A3B5C7] italic">Click to add next action...</span>}
                        </p>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs uppercase tracking-[0.2em] text-[#A3B5C7] mb-1">Due Date</label>
                        <input
                          type="date"
                          value={selectedRecord.next_action_due || ''}
                          onChange={async (e) => {
                            const val = e.target.value;
                            await pipelineApi.update(selectedRecord.id, { next_action_due: val || null });
                            setSelectedRecord(prev => ({ ...prev, next_action_due: val }));
                            fetchData();
                          }}
                          className="input-field text-sm"
                          data-testid="next-due-input"
                        />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-[0.2em] text-[#A3B5C7] mb-1">Temperature</label>
                        <div className="flex gap-1">
                          {TEMPERATURES.map(temp => (
                            <button
                              key={temp.id}
                              onClick={async () => {
                                await pipelineApi.update(selectedRecord.id, { temperature: temp.id });
                                setSelectedRecord(prev => ({ ...prev, temperature: temp.id }));
                                fetchData();
                              }}
                              className={`flex-1 py-1 text-xs rounded transition-colors ${
                                selectedRecord.temperature === temp.id
                                  ? 'border-2'
                                  : 'bg-[#0E1C33] border border-[#1A2F4C] text-[#A3B5C7] hover:text-white'
                              }`}
                              style={selectedRecord.temperature === temp.id ? { borderColor: temp.color, color: temp.color, backgroundColor: `${temp.color}15` } : {}}
                              data-testid={`temp-btn-${temp.id}`}
                            >
                              {temp.label}
                            </button>
                          ))}
                        </div>
                        {renderSuggestion(selectedRecord)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Stage Selector */}
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-[#00D4C8] mb-2">Stage</label>
                  <div className="grid grid-cols-3 gap-2">
                    {(STAGE_SETS[getStageSetKey(selectedRecord)]?.stages || activeStages).map((stage) => (
                      <button
                        key={stage.id}
                        onClick={() => handleStageChange(selectedRecord.id, stage.id)}
                        className={`px-2 py-1.5 text-xs rounded border transition-colors ${
                          selectedRecord.stage === stage.id
                            ? 'bg-[#00D4C8]/20 border-[#00D4C8] text-[#00D4C8]'
                            : 'bg-[#0A1628] border-[#1A2F4C] text-[#A3B5C7] hover:border-[#00D4C8]/50'
                        }`}
                      >
                        {stage.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Re-engagement Section — for Closed Lost */}
                {selectedRecord.stage === 'closed_lost' && (
                  <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/20" data-testid="reengagement-section">
                    <h4 className="text-sm font-medium text-amber-400 mb-3 flex items-center gap-2">
                      <Calendar className="w-4 h-4" strokeWidth={1.5} />
                      Re-engagement
                    </h4>
                    <div className="space-y-3">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!selectedRecord.flag_for_reengagement}
                          onChange={async (e) => {
                            const updates = { flag_for_reengagement: e.target.checked };
                            await pipelineApi.update(selectedRecord.id, updates);
                            setSelectedRecord(prev => ({ ...prev, flag_for_reengagement: e.target.checked }));
                            fetchData();
                          }}
                          className="accent-amber-400 w-4 h-4"
                          data-testid="reengage-checkbox"
                        />
                        <span className="text-sm text-[#D4E2F0]">Flag for re-engagement</span>
                      </label>
                      {selectedRecord.flag_for_reengagement && (
                        <>
                          <div>
                            <label className="block text-xs text-[#A3B5C7] mb-1">Re-engage after</label>
                            <input
                              type="date"
                              value={selectedRecord.reengage_after || ''}
                              onChange={async (e) => {
                                const val = e.target.value;
                                await pipelineApi.update(selectedRecord.id, { reengage_after: val });
                                setSelectedRecord(prev => ({ ...prev, reengage_after: val }));
                              }}
                              className="input-field text-sm"
                              data-testid="reengage-date"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-[#A3B5C7] mb-1">Re-engagement trigger</label>
                            <select
                              value={selectedRecord.reengage_trigger || ''}
                              onChange={async (e) => {
                                const val = e.target.value;
                                await pipelineApi.update(selectedRecord.id, { reengage_trigger: val });
                                setSelectedRecord(prev => ({ ...prev, reengage_trigger: val }));
                              }}
                              className="input-field text-sm"
                              data-testid="reengage-trigger"
                            >
                              <option value="">Select trigger...</option>
                              <option value="New project announced">New project announced</option>
                              <option value="New contact at organisation">New contact at organisation</option>
                              <option value="Budget cycle reset">Budget cycle reset</option>
                              <option value="New funding round">New funding round</option>
                              <option value="Leadership change">Leadership change</option>
                              <option value="Custom">Custom (add in notes)</option>
                            </select>
                          </div>
                        </>
                      )}
                      {selectedRecord.reengage_trigger && (
                        <p className="text-xs text-amber-400/70 italic">Will re-appear in Needs Attention when trigger is met</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs uppercase tracking-[0.2em] text-[#A3B5C7] mb-1">Contact</label>
                    <p className="text-white">{selectedRecord.contact_name || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.2em] text-[#A3B5C7] mb-1">Owner</label>
                    <p className="text-white capitalize">{selectedRecord.owner}</p>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.2em] text-[#A3B5C7] mb-1">Last Contact</label>
                    <p className="text-white">{formatDate(selectedRecord.last_contact_date)}</p>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.2em] text-[#A3B5C7] mb-1">Category</label>
                    <select
                      value={selectedRecord.category || 'Other / Unclassified'}
                      onChange={async (e) => {
                        const val = e.target.value;
                        await pipelineApi.update(selectedRecord.id, { category: val });
                        setSelectedRecord(prev => ({ ...prev, category: val }));
                        fetchData();
                      }}
                      className="input-field text-sm w-full"
                      data-testid="detail-category-select"
                    >
                      {PIPELINE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>

                {/* BD Work Types */}
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-[#A3B5C7] mb-2">BD Work Types</label>
                  <div className="flex flex-wrap gap-2">
                    {(selectedRecord.bd_work_types || []).map((type) => (
                      <span key={type} className="px-3 py-1 text-sm bg-[#00D4C8]/10 text-[#00D4C8] rounded-full">
                        {type}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Projects */}
                {selectedRecord.project_ids?.length > 0 && (
                  <div>
                    <label className="block text-xs uppercase tracking-[0.2em] text-[#A3B5C7] mb-2">Projects</label>
                    <div className="flex flex-wrap gap-2">
                      {selectedRecord.project_ids.map((pid) => (
                        <span key={pid} className="px-3 py-1 text-sm bg-white/10 text-white rounded-full">
                          {getProjectName(pid)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Deal Value Section */}
                <div className="p-4 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                  <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                    <PoundSterling className="w-4 h-4 text-[#00D4C8]" />
                    Deal Value
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-[#A3B5C7] mb-1">Estimated Value</label>
                      <p className="text-xl font-heading text-white">{formatCurrency(selectedRecord.deal_value)}</p>
                      {!isValueEligible(selectedRecord.category, selectedRecord.stage) && (
                        <p className="text-[10px] text-[#A3B5C7]/70 mt-1" data-testid="detail-deal-value-locked-note">Set once a proposal has been sent</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs text-[#A3B5C7] mb-1">Deal Type</label>
                      <p className="text-white capitalize">{selectedRecord.deal_type?.replace('_', ' ') || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-xs text-[#A3B5C7] mb-1">Probability</label>
                      <p className="text-white">{selectedRecord.probability || 0}%</p>
                    </div>
                    <div>
                      <label className="block text-xs text-[#A3B5C7] mb-1">Weighted Value</label>
                      <p className="text-[#00D4C8] font-medium">{formatCurrency(selectedRecord.weighted_value)}</p>
                    </div>
                  </div>
                </div>


                {/* Notes History with inline input */}
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-[#A3B5C7] mb-2">Notes History</label>
                  <div className="mb-2">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={inlineNoteText}
                        onChange={e => setInlineNoteText(e.target.value)}
                        placeholder="Type a note and press Enter..."
                        className="input-field text-sm flex-1"
                        data-testid="inline-note-input"
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter' && inlineNoteText.trim()) {
                            await pipelineApi.addNote(selectedRecord.id, inlineNoteText.trim());
                            setInlineNoteText('');
                            await fetchData();
                            const refreshed = (await pipelineApi.getAll()).data.find(r => r.id === selectedRecord.id);
                            if (refreshed) setSelectedRecord({ ...refreshed, bd_work_types: refreshed.bd_work_types || [] });
                          }
                        }}
                      />
                      <button
                        onClick={async () => {
                          if (!inlineNoteText.trim()) return;
                          await pipelineApi.addNote(selectedRecord.id, inlineNoteText.trim());
                          setInlineNoteText('');
                          await fetchData();
                          const refreshed = (await pipelineApi.getAll()).data.find(r => r.id === selectedRecord.id);
                          if (refreshed) setSelectedRecord({ ...refreshed, bd_work_types: refreshed.bd_work_types || [] });
                        }}
                        className="btn-primary text-xs px-3"
                        data-testid="inline-note-save"
                      ><Plus className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {(selectedRecord.notes_history || []).length > 0 ? (
                      selectedRecord.notes_history.map((note, idx) => (
                        <div key={idx} className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                          <p className="text-sm text-white">{note.text}</p>
                          <p className="text-xs text-[#A3B5C7] mt-1">
                            {note.author} • {formatDate(note.timestamp)}
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-[#A3B5C7]">No notes yet</p>
                    )}
                  </div>
                </div>

                {/* Activity Log */}
                <div data-testid="activity-log-section">
                  <label className="block text-xs uppercase tracking-[0.2em] text-[#A3B5C7] mb-2">Activity Log</label>
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {(selectedRecord.activity_log || []).length > 0 ? (
                      [...(selectedRecord.activity_log || [])].reverse().map((log, idx) => (
                        <div key={idx} className="flex items-start gap-2 p-2 rounded bg-[#0A1628]">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#00D4C8] mt-2 flex-shrink-0" />
                          <div>
                            <p className="text-xs text-[#D4E2F0]">{log.details}</p>
                            <p className="text-[10px] text-[#A3B5C7]">
                              {log.author} • {formatDate(log.timestamp)}
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-[#A3B5C7]">No activity logged yet</p>
                    )}
                  </div>
                </div>

                {/* Edit Button */}
                <button
                  onClick={() => openEditDialog(selectedRecord)}
                  className="w-full btn-secondary flex items-center justify-center gap-2"
                >
                  <Edit2 className="w-4 h-4" /> Edit Record
                </button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Add Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent className="bg-[#0E1C33] border-[#1A2F4C] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white">Add Note</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              className="input-field resize-none w-full"
              rows={4}
              placeholder="Enter your note..."
            />
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setShowNoteDialog(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleAddNote} className="btn-primary" disabled={!noteText.trim()}>Add Note</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-[#0E1C33] border-[#1A2F4C] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white">
              {editingRecord ? 'Edit Pipeline Record' : 'Add Pipeline Record'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Target */}
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#00D4C8] mb-2">Target</label>
              <SearchableSelect
                options={targets.map(t => ({ value: t.id, label: t.name, type: t.type || '' }))}
                value={formData.target_id}
                onChange={(v) => setFormData(prev => ({ ...prev, target_id: v }))}
                placeholder="Search targets..."
                typeKey="type"
                filterTypes={['Foundation', 'Brand', 'Broadcaster', 'Technology', 'Individual', 'Other']}
                testId="pipeline-target-select"
              />
            </div>

            {/* Contact Name */}
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#00D4C8] mb-2">Contact Name</label>
              <input
                type="text"
                value={formData.contact_name}
                onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                className="input-field"
                placeholder="Primary contact"
              />
            </div>

            {/* BD Work Types */}
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#00D4C8] mb-2">BD Work Types</label>
              <div className="flex flex-wrap gap-2">
                {BD_WORK_TYPES.map((type) => {
                  const isSelected = formData.bd_work_types?.includes(type);
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => toggleBdWorkType(type)}
                      className={`px-3 py-1.5 text-sm rounded-lg border transition-colors flex items-center gap-1 ${
                        isSelected
                          ? 'bg-[#00D4C8]/20 border-[#00D4C8] text-[#00D4C8]'
                          : 'bg-[#0A1628] border-[#1A2F4C] text-[#A3B5C7] hover:border-[#00D4C8]/50'
                      }`}
                    >
                      {isSelected && <Check className="w-3 h-3" />}
                      {type}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Owner & Temperature */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#00D4C8] mb-2">Owner</label>
                <Select value={formData.owner} onValueChange={(v) => setFormData(prev => ({ ...prev, owner: v }))}>
                  <SelectTrigger className="w-full bg-[#0A1628] border-[#1A2F4C] text-[#D4E2F0]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0E1C33] border-[#1A2F4C]">
                    {ownerOptions.map(o => (
                      <SelectItem key={o} value={o} className="text-[#D4E2F0] hover:bg-[#1A2F4C]">{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#00D4C8] mb-2">Temperature</label>
                <Select value={formData.temperature} onValueChange={(v) => setFormData(prev => ({ ...prev, temperature: v }))}>
                  <SelectTrigger className="w-full bg-[#0A1628] border-[#1A2F4C] text-[#D4E2F0]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0E1C33] border-[#1A2F4C]">
                    {TEMPERATURES.map((temp) => (
                      <SelectItem key={temp.id} value={temp.id} className="text-[#D4E2F0] hover:bg-[#1A2F4C]">
                        {temp.emoji} {temp.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Deal Value Section */}
            <div className="p-4 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
              <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                <PoundSterling className="w-4 h-4 text-[#00D4C8]" />
                Deal Value
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-[#A3B5C7] mb-1">Estimated Value (£)</label>
                  <input
                    type="number"
                    value={isValueEligible(formData.category, formData.stage) ? formData.deal_value : ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, deal_value: e.target.value }))}
                    disabled={!isValueEligible(formData.category, formData.stage)}
                    className={`input-field ${!isValueEligible(formData.category, formData.stage) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    placeholder="0"
                    data-testid="deal-value-input"
                  />
                  {!isValueEligible(formData.category, formData.stage) && (
                    <p className="text-[10px] text-[#A3B5C7]/70 mt-1" data-testid="deal-value-locked-note">Available once a proposal has been sent</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-[#A3B5C7] mb-1">Deal Type</label>
                  <Select value={formData.deal_type} onValueChange={(v) => setFormData(prev => ({ ...prev, deal_type: v }))}>
                    <SelectTrigger className="w-full bg-[#0E1C33] border-[#1A2F4C] text-[#D4E2F0]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0E1C33] border-[#1A2F4C]">
                      <SelectItem value="one_off" className="text-[#D4E2F0]">One-off</SelectItem>
                      <SelectItem value="recurring" className="text-[#D4E2F0]">Recurring</SelectItem>
                      <SelectItem value="retainer" className="text-[#D4E2F0]">Retainer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Next Action */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#00D4C8] mb-2">Next Action</label>
                <input
                  type="text"
                  value={formData.next_action}
                  onChange={(e) => setFormData(prev => ({ ...prev, next_action: e.target.value }))}
                  className="input-field"
                  placeholder="What's the next step?"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#00D4C8] mb-2">Due Date</label>
                <input
                  type="date"
                  value={formData.next_action_due}
                  onChange={(e) => setFormData(prev => ({ ...prev, next_action_due: e.target.value }))}
                  className="input-field"
                />
              </div>
            </div>

            {/* Category */}
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#00D4C8] mb-2">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="input-field"
                data-testid="form-category"
              >
                {PIPELINE_CATEGORIES.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#00D4C8] mb-2">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="input-field resize-none"
                rows={3}
                placeholder="Additional context..."
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-[#1A2F4C]">
              <button onClick={closeDialog} className="btn-secondary">Cancel</button>
              <button onClick={handleCreateOrUpdate} className="btn-primary" disabled={!formData.target_id}>
                {editingRecord ? 'Save Changes' : 'Add to Pipeline'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Target Confirmation Dialog */}
      {showDeleteTargetConfirm && selectedRecord && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 pointer-events-auto" data-testid="pipeline-delete-confirm-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#0E1C33] border border-[#1A2F4C] rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-400" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg text-white">Delete Target</h3>
            </div>
            <p className="text-[#D4E2F0] text-sm leading-relaxed mb-6">
              Are you sure you want to delete <span className="text-white font-medium">{selectedRecord.target_name}</span>? This will also remove them from your pipeline and cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteTargetConfirm(false)}
                className="btn-secondary text-sm"
                data-testid="pipeline-delete-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteTargetFromPanel}
                disabled={deletingTarget}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-red-500 hover:bg-red-600 text-white transition-colors flex items-center gap-2"
                data-testid="pipeline-delete-confirm-btn"
              >
                {deletingTarget ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Delete Target
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Briefing Modal */}
      {briefingTarget && (
        <BriefingModal
          targetId={briefingTarget.id}
          targetName={briefingTarget.name}
          onClose={() => setBriefingTarget(null)}
        />
      )}
    </div>
  );
};

export default PipelinePage;
